import type { LiaSujetView, LiaTourView } from '@taki/ui';
import type { RecordLink, ToolStep } from '../surfaces/askTakiClient.js';
import type { ConversationResume } from './sources.js';

/**
 * LES SUJETS DE LIA, vus par le centre : fonctions pures sur les sujets du
 * port (`LiaConversationsPort`) et sur les tours de la conversation en cours.
 */

/** Un tour tel que la conversation le tient en memoire : les etapes d'outils completes, le temps d'une session. */
export interface TourLia {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  at?: number;
  steps?: ToolStep[];
  records?: RecordLink[];
}

export const TITRE_SUJET_MAX = 48;
export const TITRE_SUJET_DEFAUT = 'Nouveau sujet';

/**
 * Le titre d'un sujet, tire de sa premiere question : la premiere ligne, sans
 * ponctuation finale, coupee au mot. Le fondateur renomme ensuite s'il veut.
 */
export function titreDepuisQuestion(question: string): string {
  const ligne = question.replace(/\s+/g, ' ').trim().replace(/[\s?!.:;,]+$/u, '');
  if (ligne === '') return TITRE_SUJET_DEFAUT;
  if (ligne.length <= TITRE_SUJET_MAX) return ligne;
  const coupe = ligne.slice(0, TITRE_SUJET_MAX);
  const dernierEspace = coupe.lastIndexOf(' ');
  return `${(dernierEspace > TITRE_SUJET_MAX / 2 ? coupe.slice(0, dernierEspace) : coupe).trimEnd()}…`;
}

/** Les outils d'un tour, resumes pour l'historique (le detail ne survit pas a la session). */
export function resumeDesOutils(steps: readonly ToolStep[] | undefined): LiaTourView['outils'] | undefined {
  if (!steps || steps.length === 0) return undefined;
  const ecriture = (kind: ToolStep['kind']) => kind === 'create' || kind === 'update' || kind === 'studio';
  return {
    lectures: steps.filter((step) => step.state === 'done' && !ecriture(step.kind)).length,
    ecritures: steps.filter((step) => step.state === 'done' && ecriture(step.kind)).length,
    echecs: steps.filter((step) => step.state === 'error').length,
  };
}

/** Un tour en memoire devient un tour conserve : texte, instant, fiches citees, compte des outils. */
export function tourPourStockage(tour: TourLia): LiaTourView {
  const records = (tour.records ?? []).slice(0, 20).map((record) => ({
    id: record.id, title: record.title.slice(0, 200), appId: record.appId, doorId: record.doorId,
    ...(record.doorName ? { doorName: record.doorName.slice(0, 80) } : {}),
    ...(record.family ? { family: record.family } : {}),
  }));
  const outils = resumeDesOutils(tour.steps);
  return {
    id: tour.id, role: tour.role, text: tour.text,
    ...(tour.at !== undefined ? { at: tour.at } : {}),
    ...(records.length ? { records } : {}),
    ...(outils ? { outils } : {}),
  };
}

/** Un tour conserve redevient un tour en memoire, sans etapes : elles ne sont pas conservees. */
export function tourDepuisStockage(tour: LiaTourView): TourLia {
  return {
    id: tour.id, role: tour.role, text: tour.text,
    ...(tour.at !== undefined ? { at: tour.at } : {}),
    ...(tour.records ? { records: tour.records } : {}),
    ...(tour.outils ? { outilsResumes: tour.outils } : {}),
  } as TourLia & { outilsResumes?: LiaTourView['outils'] };
}

/** Ce que le tri et le choix du sujet a ouvrir ont besoin de savoir d'un sujet. */
export type SujetTriable = { id: string; archiveLe: number | null; modifieLe: number };

/** Les sujets vivants, du plus recent au plus ancien, puis les archives : ce que le rail liste. */
export function trierSujets<T extends SujetTriable>(sujets: readonly T[]): T[] {
  return [...sujets].sort((a, b) => {
    if ((a.archiveLe === null) !== (b.archiveLe === null)) return a.archiveLe === null ? -1 : 1;
    return b.modifieLe - a.modifieLe;
  });
}

/** Un sujet Lia dans la liste du rail. */
export function resumeDeSujet(sujet: Pick<LiaSujetView, 'id' | 'titre' | 'archiveLe'>, pointLime = false): ConversationResume {
  return { id: sujet.id, source: 'lia', titre: sujet.titre, nonLus: 0, pointLime, glyphe: 'lia', archivee: sujet.archiveLe !== null };
}

/**
 * Le sujet a ouvrir a l'arrivee : celui que le poste regardait s'il existe
 * encore et n'est pas archive, sinon le plus recent des vivants, sinon rien
 * (le centre ouvre alors un sujet neuf en memoire).
 */
export function sujetAOuvrir(sujets: readonly SujetTriable[], memorise: string | undefined): string | null {
  const vivants = trierSujets(sujets).filter((sujet) => sujet.archiveLe === null);
  if (memorise && vivants.some((sujet) => sujet.id === memorise)) return memorise;
  return vivants[0]?.id ?? null;
}

/**
 * LE SUJET COURANT DE LA FENETRE (verification independante, ecart B) : un
 * sujet ARCHIVE n'est jamais la conversation courante (les archives se lisent
 * dans l'app Canaux). Le sujet memorise s'il est vivant ; memorise mais archive
 * (depuis Canaux ou ailleurs) : aucun, et la fenetre ouvre un sujet neuf ; rien
 * de memorise : le plus recent des vivants, sinon aucun.
 */
export function sujetCourant(sujets: readonly SujetTriable[], memorise: string | undefined): string | null {
  const retenu = memorise === undefined ? undefined : sujets.find((sujet) => sujet.id === memorise);
  if (retenu) return retenu.archiveLe === null ? retenu.id : null;
  return sujetAOuvrir(sujets, memorise) ?? memorise ?? null;
}

/** L'heure d'un tour, « 16:42 », ou rien pour un tour d'avant ce chantier. */
export function heureDuTour(at: number | undefined, locale = 'fr-FR'): string {
  if (at === undefined) return '';
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(at));
}

/**
 * Le separateur de jour entre deux tours : « Aujourd'hui », « Hier », ou la
 * date longue, rendu quand le jour change (maquette hub-a).
 */
export function separateurDeJour(at: number | undefined, precedent: number | undefined, maintenant = Date.now(), locale = 'fr-FR'): string | null {
  if (at === undefined) return null;
  const jour = (value: number) => new Date(value).toDateString();
  if (precedent !== undefined && jour(precedent) === jour(at)) return null;
  const aujourdHui = jour(maintenant);
  if (jour(at) === aujourdHui) return 'Aujourd’hui';
  if (jour(at) === jour(maintenant - 86_400_000)) return 'Hier';
  return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(at));
}

/** Les initiales d'un nom : « Taki » devient « TA », « Ada Lovelace » devient « AL ». */
export function initialesDe(nom: string | null | undefined, repli = '?'): string {
  const mots = (nom ?? '').trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return repli;
  if (mots.length === 1) return mots[0]!.slice(0, 2).toLocaleUpperCase('fr');
  return `${mots[0]![0]}${mots[1]![0]}`.toLocaleUpperCase('fr');
}

/**
 * Le titre que porte « Reponse prete : ... » : celui du rail. Un sujet neuf ne
 * recoit son titre qu'APRES la question (`onSujetChange`) ; on le tire donc ici
 * de la premiere question, avec la meme regle, avant l'appel.
 */
export function titreDuSujetPourLaNotification(titre: string, tours: readonly TourLia[]): string {
  if (titre !== TITRE_SUJET_DEFAUT) return titre;
  const premiere = tours.find((tour) => tour.role === 'user');
  return premiere ? titreDepuisQuestion(premiere.text) : titre;
}
